import { useCallback, useEffect, useState } from 'react'
import { Cross2Icon, PlusIcon } from '@radix-ui/react-icons'
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  UserServiceUserStatus,
  useUserServiceUsersByTypeQuery,
} from 'zma-react-sdk/user-service'
import { useTranslation } from '@/hooks/use-translation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DataTableFacetedFilter,
  DataTableViewOptions,
  DataTablePagination,
} from '@/components/crud'
import { createColumns } from './agencies-columns'
import { AgencyDialog } from './agency-dialog'
import { Agency } from './schema'

interface AgenciesTableProps {
  columns: ColumnDef<Agency>[]
  onEdit?: (agency: Agency) => void
}

export function AgenciesTable({ columns: _, onEdit }: AgenciesTableProps) {
  const [rowSelection, setRowSelection] = useState({})
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [sorting, setSorting] = useState<SortingState>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null)

  const { t } = useTranslation()

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset pagination when search or filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm, columnFilters, sorting])

  // Calculate pagination parameters
  const skip = (currentPage - 1) * pageSize

  // Extract filters from table state
  const statusFilter = columnFilters.find((filter) => filter.id === 'status')
    ?.value as UserServiceUserStatus[] | undefined

  // Fetch agencies data
  const { data, loading, error } = useUserServiceUsersByTypeQuery({
    variables: {
      pagination: {
        limit: pageSize,
        skip: skip,
      },
      input: {
        type: 'Agency',
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(statusFilter?.length === 1 && { status: statusFilter[0] }),
      },
    },
    fetchPolicy: 'cache-and-network',
  })

  // Transform GraphQL data to match our schema
  const agencies: Agency[] =
    data?.userServiceUsersByType?.data?.map((user) => ({
      id: user.id,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
      email: user.email || '',
      phoneNumber: user.phoneNumber || '',
      status: user.status as any,
      avatarUrl: user.avatarUrl || '',
      organizationId: user.organizationId || '',
      createdAt: user.createdAt || undefined,
      updatedAt: user.updatedAt || undefined,
    })) || []

  // Handle search changes
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  // Handle dialog operations
  const handleCreateAgency = () => {
    setSelectedAgency(null)
    setDialogOpen(true)
  }

  const handleEditAgency = (agency: Agency) => {
    setSelectedAgency(agency)
    setDialogOpen(true)
    onEdit?.(agency)
  }

  const handleDialogSuccess = () => {
    // The dialog will close itself and refetch data
    setSelectedAgency(null)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1)
  }

  // Create columns with proper handlers
  const columns = createColumns(t, handleEditAgency)

  const table = useReactTable({
    data: agencies,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: false, // Let the table handle client-side sorting
    manualFiltering: true,
    pageCount: -1,
  })

  // Status options for filtering
  const statusOptions = [
    { label: t('agencies.status.active'), value: 'Active' },
    { label: t('agencies.status.suspended'), value: 'Suspended' },
    { label: t('agencies.status.pending'), value: 'Pending' },
    { label: t('agencies.status.disabled'), value: 'Disabled' },
  ]

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='flex flex-1 items-center space-x-2'>
          <Input
            placeholder={t('agencies.filterAgencies')}
            value={searchTerm}
            onChange={(event) => handleSearchChange(event.target.value)}
            className='h-8 w-[150px] lg:w-[250px]'
          />
          {columnFilters.find((filter) => filter.id === 'status') && (
            <DataTableFacetedFilter
              column={table.getColumn('status')}
              title={t('common.status')}
              options={statusOptions}
            />
          )}
          {table.getColumn('status') &&
            !columnFilters.find((filter) => filter.id === 'status') && (
              <DataTableFacetedFilter
                column={table.getColumn('status')}
                title={t('common.status')}
                options={statusOptions}
              />
            )}
          {columnFilters.length > 0 && (
            <Button
              variant='ghost'
              onClick={() => {
                table.resetColumnFilters()
                setSearchTerm('')
              }}
              className='h-8 px-2 lg:px-3'
            >
              {t('common.reset')}
              <Cross2Icon className='ml-2 h-4 w-4' />
            </Button>
          )}
        </div>
        <div className='flex items-center space-x-2'>
          <DataTableViewOptions table={table} />
          <Button onClick={handleCreateAgency} size='sm' className='h-8'>
            <PlusIcon className='mr-2 h-4 w-4' />
            {t('agencies.createAgency')}
          </Button>
        </div>
      </div>

      <div className='rounded-md border'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={header.column.columnDef.meta?.className}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className='group/row'
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cell.column.columnDef.meta?.className}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'
                >
                  {loading
                    ? t('common.loading')
                    : error
                      ? t('agencies.errorLoadingAgencies')
                      : t('agencies.noResults')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <DataTablePagination
        table={table}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <AgencyDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        agency={selectedAgency}
        onSuccess={handleDialogSuccess}
      />
    </div>
  )
}
